import React, { useState } from 'react'
import { Box, Button, Paper, Typography } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { HelpCircle as HelpCircleIcon } from 'react-feather'
import { Alert } from '@material-ui/lab'

import {
  Container,
  ContentHeader,
  DialogLogs,
  Divider,
  DriveTour,
  LoadingFeedback,
  MenuButton,
  Page,
  Permitted,
} from 'components'

import {
  AddCategoryButton,
  ConfigurationSkeleton,
  ManageConfiguration,
  CategoryTable,
} from './components'

import { routes } from 'Routes'

import useFetch from 'hooks/useFetch'
import usePagination from 'hooks/usePagination'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import theme from 'theme'

const ComplaintConfig = () => {
  const [openLogDialog, setOpenLogDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const [responseLog, setResponseLog] = useState([])
  const [openTour, setOpenTour] = useState(false)

  const {
    COMPLAINT_CONFIG_STEPS_OBJECT,
    COMPLAINT_CONFIG_DRIVER_STEP_1,
    COMPLAINT_CONFIG_DRIVER_STEP_4,
  } = constants.tours.complaint

  const snackbar = useSnackbar()
  const history = useHistory()

  const { page, perPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5)

  const { response, isLoading, refresh } = useFetch(
    service.dponet.complaintConfigurations.mainConfiguration,
    [],
  )

  const {
    response: categoriesResponse,
    isLoading: isLoadingCategories,
    refresh: refreshCategories,
  } = useFetch(service.dponet.complaintCategories.get, { page, perPage }, [
    page,
    perPage,
  ])

  const complaintConfiguration = response?.data?.complaintConfiguration
  const categories = categoriesResponse?.data?.complaintCategories || []
  const totalCategories = categoriesResponse?.data?.meta?.totalCount || 0

  const isLoadingRequests = isLoading || isLoadingCategories

  const backToComplaints = () => {
    return history.push(routes.complaint.all)
  }

  const handleOpenTour = () => {
    setOpenTour(true)
  }

  const handleOpenLogs = async () => {
    try {
      setLoading(true)
      const responseLogs = await service.dponet.complaintConfigurations.logs({})
      const updatedlogs = helpers.logs.changeLogs(
        responseLogs?.data?.dponetAudits || [],
      )
      setResponseLog(updatedlogs)
      setOpenLogDialog(true)
    } catch (error) {
      setOpenLogDialog(false)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error, false) ||
          error?.error?.message,
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Page title="Configurações Canal de Denúncia">
      <Container maxWidth={false}>
        <LoadingFeedback open={loading} />
        <ContentHeader
          title="Configurações Gerais"
          id={COMPLAINT_CONFIG_DRIVER_STEP_1}
        >
          {!isLoadingRequests && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<HelpCircleIcon size={20} />}
                onClick={handleOpenTour}
                fullWidth
              >
                Tutorial
              </Button>
              <MenuButton>
                <Button color="secondary" fullWidth onClick={handleOpenLogs}>
                  Histórico de alterações
                </Button>
              </MenuButton>
            </>
          )}
        </ContentHeader>
        {isLoadingRequests ? (
          <ConfigurationSkeleton />
        ) : (
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(4)}>
            <Alert severity="warning">
              <Typography variant="body2">
                <strong>ATENÇÃO!</strong>
                <br />
                Apenas os responsáveis por cada categoria terão acesso às
                denúncias. Nem mesmo o representante e o{' '}
                <strong>Encarregado de Proteção de Dados</strong> poderão
                visualizar.
              </Typography>
            </Alert>
            <ManageConfiguration
              complaintConfiguration={complaintConfiguration}
              setLoading={setLoading}
              refresh={refresh}
            />
            <Paper id={COMPLAINT_CONFIG_DRIVER_STEP_4}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={2}
              >
                <Typography variant="subtitle1" color="primary">
                  Gerenciamento de Categorias
                </Typography>

                <AddCategoryButton onSuccess={refreshCategories} />
              </Box>
              <Divider />
              <CategoryTable
                categories={categories}
                totalCount={totalCategories}
                isLoading={isLoadingCategories}
                page={page}
                perPage={perPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                refresh={refreshCategories}
              />
            </Paper>

            <Box
              display="flex"
              justifyContent="flex-end"
              gridGap={theme.spacing(1)}
            >
              <Button variant="outlined" onClick={backToComplaints}>
                Voltar
              </Button>
              <Permitted
                someTags={[
                  constants.permissions.COMPLAINTS.MANAGE_CONFIGURATIONS,
                  constants.permissions.COMPLAINTS.ACTIVATE_COMPLAINTS,
                ]}
              >
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  form="manage-configuration-form"
                >
                  Salvar
                </Button>
              </Permitted>
            </Box>
          </Box>
        )}
      </Container>
      {!loading && (
        <DialogLogs
          logs={
            helpers.functions.isEmpty(responseLog) ? undefined : responseLog
          }
          open={openLogDialog}
          setOpen={setOpenLogDialog}
          showRegisterSession={false}
          refresh={handleOpenLogs}
        />
      )}
      <DriveTour
        stepsMatrix={COMPLAINT_CONFIG_STEPS_OBJECT}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Page>
  )
}

export default ComplaintConfig
